import {
  CloseOutlined,
  CopyOutlined,
  DropboxOutlined,
  EditOutlined,
  LaptopOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { AutoComplete, Button, Form, Input, Modal, Select, Tag, Tooltip, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import SummaryLayout from '../../../components/layout/SummaryLayout';
import { getData, postData } from '../../../config/api-connect';
import { getColumnFilters, getColumnSearchProps } from '../../../utils/filters';
import '../../../variables.css';
import './assets.css';

const { TextArea } = Input;
const { Option } = Select;

const statusColorMap = {
  Active: 'success',
  Damaged: 'error',
  Reserved: 'warning',
  Retired: 'default',
};

function AssetsManagement() {
  const [assets, setAssets] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [stats, setStats] = useState({ total: 0, assigned: 0, in_stock: 0 });
  const [opts, setOpts] = useState({
    asset_types: [],
    brands: [],
    departments: [],
    locations: [],
    employees: [],
  });
  const filtersRef = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ page, page_size: 10 });
    if (filters.asset_type) p.set('asset_type', filters.asset_type);
    if (filters.brand) p.set('brand', filters.brand);
    if (filters.location_id) p.set('location_id', filters.location_id);
    if (filters.department) p.set('department', filters.department);
    if (filters.status) p.set('status', filters.status);
    if (filters.search) p.set('search', filters.search);
    try {
      const r = await getData('/finance/get_assets?' + p.toString());
      if (r.success) {
        setAssets(r.result);
        setTotal(r.count);
      }
    } catch {
      message.error('Failed to fetch assets');
    }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [page, filters, load]);

  useEffect(() => {
    loadStats();
    loadOpts();
  }, []);

  const loadStats = async () => {
    try {
      const r = await getData('/finance/get_asset_stats');
      if (r.success) setStats(r.result);
    } catch {
      /* ignore */
    }
  };

  const loadOpts = async () => {
    try {
      const r = await getData('/finance/get_asset_filters');
      if (r.success) setOpts(r.result);
    } catch {
      /* ignore */
    }
  };

  const fm = (v) => (v ?? 0).toLocaleString('en-IN');

  const openEdit = (r) => {
    setSelectedAsset(r);
    editForm.setFieldsValue({
      brand: r.brand_name || '',
      model_name: r.model_name,
      serial_number: r.serial_number,
      assigned_to: r.assigned_to,
      location: r.location,
      status: r.status || 'Active',
      comments: r.comments,
    });
    setEditModalOpen(true);
  };

  const copySerial = () => {
    const s = editForm.getFieldValue('serial_number');
    if (s) {
      navigator.clipboard.writeText(s);
      message.success('Serial number copied');
    }
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'asset_type_name',
      key: 'asset_type_name',
      width: 110,
      filteredValue: filters.asset_type ? [filters.asset_type] : null,
      ...getColumnFilters(opts.asset_types.map((t) => ({ text: t, value: t }))),
    },
    {
      title: 'Brand',
      dataIndex: 'brand_name',
      key: 'brand_name',
      width: 120,
      filteredValue: filters.brand ? [filters.brand] : null,
      ...getColumnFilters(opts.brands.map((b) => ({ text: b, value: b }))),
    },
    {
      title: 'Model',
      dataIndex: 'model_name',
      key: 'model_name',
      width: 150,
      filteredValue: filters.search ? [filters.search] : null,
      ...getColumnSearchProps('model_name', true),
    },
    {
      title: 'Serial No.',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 160,
      ellipsis: { showTitle: true },
      render: (t) => (
        <span className="assets-serial" title={t}>
          {t || '—'}
        </span>
      ),
      filteredValue: filters.search ? [filters.search] : null,
      ...getColumnSearchProps('serial_number', true),
    },
    {
      title: 'Location',
      dataIndex: 'location_name',
      key: 'location_name',
      width: 160,
      filteredValue: filters.location_id ? [filters.location_id] : null,
      ...getColumnFilters(opts.locations.map((l) => ({ text: l.name, value: l.id }))),
      render: (t) => (
        <Tooltip title={t}>
          <span>{t || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to_name',
      key: 'assigned_to_name',
      width: 140,
      filteredValue: filters.search ? [filters.search] : null,
      ...getColumnSearchProps('assigned_to_name', true),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 100,
      render: (d) =>
        d && d !== 'Unknown' ? (
          <Tag color="processing" className="assets-tag">
            {d}
          </Tag>
        ) : null,
      filteredValue: filters.department ? [filters.department] : null,
      ...getColumnFilters(opts.departments.map((d) => ({ text: d, value: d }))),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => (
        <Tag color={statusColorMap[s] || 'default'} className="assets-tag">
          {s}
        </Tag>
      ),
      filteredValue: filters.status ? [filters.status] : null,
      ...getColumnFilters(
        ['Active', 'Damaged', 'Reserved', 'Retired'].map((s) => ({ text: s, value: s })),
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      ellipsis: true,
      width: 200,
      render: (t) =>
        t ? (
          <span className="assets-comments-text">{t.length > 30 ? t.slice(0, 30) + '…' : t}</span>
        ) : (
          <span className="assets-muted">—</span>
        ),
      filteredValue: filters.search ? [filters.search] : null,
      ...getColumnSearchProps('comments', true),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, r) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
          Edit
        </Button>
      ),
    },
  ];

  const tableComponents = {
    header: {
      cell: (props) => {
        const { className, ...rest } = props;
        return <th {...rest} className={`${className || ''} assets-thead-cell`} />;
      },
    },
  };

  const handleTableChange = (pagination, tableFilters) => {
    const f = {};
    if (tableFilters.asset_type_name?.length) f.asset_type = tableFilters.asset_type_name[0];
    if (tableFilters.brand_name?.length) f.brand = tableFilters.brand_name[0];
    if (tableFilters.location_name?.length) f.location_id = tableFilters.location_name[0];
    if (tableFilters.department?.length) f.department = tableFilters.department[0];
    if (tableFilters.status?.length) f.status = tableFilters.status[0];
    const searchVal =
      tableFilters.model_name?.[0] ||
      tableFilters.serial_number?.[0] ||
      tableFilters.assigned_to_name?.[0] ||
      tableFilters.comments?.[0];
    if (searchVal) f.search = searchVal;
    if (JSON.stringify(f) !== JSON.stringify(filtersRef.current)) {
      filtersRef.current = f;
      setPage(1);
    }
    setFilters(f);
  };

  const statsConfig = [
    {
      key: 'total',
      icon: <LaptopOutlined className="assets-inv-total-icon" />,
      title: 'Total Assets',
    },
    {
      key: 'assigned',
      icon: <UserOutlined className="assets-inv-assigned-icon" />,
      title: 'Assigned',
    },
    {
      key: 'in_stock',
      icon: <DropboxOutlined className="assets-inv-in-stock-icon" />,
      title: 'In Stock',
    },
  ];

  return (
    <div className="assets-page">
      <SummaryLayout
        title="Asset Inventory"
        headerContent={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setAddModalOpen(true);
              addForm.resetFields();
            }}
          >
            Add New Asset
          </Button>
        }
        statsConfig={statsConfig}
        totals={stats}
        statsLoading={loading}
        formatter={fm}
        tableProps={{
          rowKey: 'id',
          columns,
          data: assets,
          loading,
          size: 'middle',
          scroll: { x: 1000 },
          components: tableComponents,
          rowClassName: (_, i) => (i % 2 === 0 ? '' : 'alt-row'),
          onChange: handleTableChange,
          pagination: {
            current: page,
            pageSize: 10,
            total,
            onChange: (p) => setPage(p),
            showSizeChanger: false,
            size: 'small',
            showTotal: (t, r) => (
              <span className="assets-pagination-text">
                Showing <b>{r[1] - r[0] + 1}</b> of <b>{t}</b> assets
              </span>
            ),
          },
        }}
      />

      {/* ── Add Modal ── */}
      <Modal
        title={null}
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        footer={null}
        width={680}
        closable={false}
        destroyOnClose
      >
        <div className="asset-modal-header">
          <h3 className="asset-modal-title">Add New Asset</h3>
          <CloseOutlined className="asset-modal-close" onClick={() => setAddModalOpen(false)} />
        </div>
        <Form form={addForm} layout="vertical">
          <div className="asset-modal-section">Asset Information</div>
          <div className="asset-form-row">
            <Form.Item
              label="Asset Type"
              name="asset_type"
              rules={[{ required: true, message: 'Please select' }]}
              className="asset-form-item"
            >
              <AutoComplete
                placeholder="Select or type new asset type"
                options={opts.asset_types.map((v) => ({ value: v }))}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
              label="Brand"
              name="brand"
              rules={[{ required: true, message: 'Please enter brand' }]}
              className="asset-form-item"
            >
              <AutoComplete
                placeholder="Select or type new asset brand"
                options={opts.brands.map((v) => ({ value: v }))}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
          </div>
          <div className="asset-form-row">
            <Form.Item
              label="Model"
              name="model_name"
              rules={[{ required: true, message: 'Please enter model' }]}
              className="asset-form-item"
            >
              <Input placeholder="e.g. MacBook Pro 16" />
            </Form.Item>
            <Form.Item label="Serial Number" name="serial_number" className="asset-form-item">
              <Input placeholder="UUID or SN" />
            </Form.Item>
          </div>
          <div className="asset-modal-section">Assignment</div>
          <div className="asset-form-row">
            <Form.Item label="Location" name="location" className="asset-form-item">
              <Select
                placeholder="Select location"
                showSearch
                filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}
              >
                {opts.locations.map((l) => (
                  <Option key={l.id} value={l.id}>
                    {l.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Assigned To" name="assigned_to" className="asset-form-item">
              <Select
                placeholder="Search employee"
                showSearch
                filterOption={(i, o) => {
                  const input = i.toLowerCase();
                  const children = o.props && o.props.children;
                  const text = Array.isArray(children)
                    ? children.join('')
                    : typeof children === 'string'
                      ? children
                      : '';
                  const mobile = ((o.props && o.props['data-mobile']) || '').toLowerCase();
                  return text.toLowerCase().includes(input) || mobile.includes(input);
                }}
              >
                {opts.employees.map((e) => (
                  <Option key={e.id} value={e.id} data-mobile={e.mobile || e.mobile_no || ''}>
                    {e.name ? e.name : e.mobile_no ? e.mobile_no : 'Unknown'}{' '}
                    {e.department && e.department !== 'Unknown' ? `: (${e.department})` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className="asset-form-row">
            <Form.Item
              label="Status"
              name="status"
              initialValue="Active"
              className="asset-form-item"
            >
              <Select>
                <Option value="Active">Active</Option>
                <Option value="Reserved">Reserved</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item label="Comments" name="comments" className="asset-form-item-last">
            <TextArea rows={2} placeholder="Warranty, procurement notes…" />
          </Form.Item>
        </Form>
        <div className="asset-modal-footer">
          <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
          <Button
            type="primary"
            onClick={() =>
              addForm.validateFields().then(async (v) => {
                try {
                  const payload = {
                    ...v,
                    asset_type: Array.isArray(v.asset_type) ? v.asset_type[0] : v.asset_type,
                    brand: Array.isArray(v.brand) ? v.brand[0] : v.brand,
                  };
                  const r = await postData('/finance/create_asset/', payload);
                  if (r.success) {
                    message.success('Asset created');
                    setAddModalOpen(false);
                    addForm.resetFields();
                    load();
                    loadStats();
                    loadOpts();
                  } else message.error(r.message || 'Failed');
                } catch {
                  message.error('Failed to create asset');
                }
              })
            }
          >
            Save Asset
          </Button>
        </div>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        title={null}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        footer={null}
        width={680}
        closable={false}
        destroyOnClose
      >
        <div className="asset-modal-header">
          <div>
            <h3 className="asset-modal-title">Edit Asset</h3>
            {selectedAsset && (
              <span className="asset-modal-subtitle">{selectedAsset.model_name}</span>
            )}
          </div>
          <CloseOutlined className="asset-modal-close" onClick={() => setEditModalOpen(false)} />
        </div>
        <Form form={editForm} layout="vertical">
          <div className="asset-modal-section">Asset Details</div>
          <div className="asset-form-row">
            <Form.Item label="Brand" name="brand" className="asset-form-item">
              <AutoComplete
                placeholder="Select or type brand"
                options={opts.brands.map((v) => ({ value: v }))}
                filterOption={(inputValue, option) =>
                  option.value.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item label="Model" name="model_name" className="asset-form-item">
              <Input />
            </Form.Item>
          </div>
          <Form.Item label="Serial Number" name="serial_number" className="asset-form-item">
            <Input suffix={<CopyOutlined className="asset-modal-close" onClick={copySerial} />} />
          </Form.Item>
          <div className="asset-modal-section">Assignment & Status</div>
          <Form.Item label="Assigned To" name="assigned_to" className="asset-form-item">
            <Select
              placeholder="Search employee"
              showSearch
              filterOption={(i, o) => {
                const input = i.toLowerCase();
                const children = o.props && o.props.children;
                const text = Array.isArray(children)
                  ? children.join('')
                  : typeof children === 'string'
                    ? children
                    : '';
                const mobile = ((o.props && o.props['data-mobile']) || '').toLowerCase();
                return text.toLowerCase().includes(input) || mobile.includes(input);
              }}
            >
              {opts.employees.map((e) => (
                <Option key={e.id} value={e.id} data-mobile={e.mobile || e.mobile_no || ''}>
                  {e.name ? e.name : e.mobile_no ? e.mobile_no : 'Unknown'}{' '}
                  {e.department && e.department !== 'Unknown' ? `: (${e.department})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div className="asset-form-row">
            <Form.Item label="Location" name="location" className="asset-form-item">
              <Select>
                {opts.locations.map((l) => (
                  <Option key={l.id} value={l.id}>
                    {l.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status" name="status" className="asset-form-item">
              <Select>
                {['Active', 'Damaged', 'Reserved', 'Retired'].map((s) => (
                  <Option key={s} value={s}>
                    {s}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <Form.Item label="Comments" name="comments" className="asset-form-item-last">
            <TextArea rows={2} />
          </Form.Item>
          <div className="asset-modal-section">History</div>
          {selectedAsset?.logs?.length > 0 ? (
            selectedAsset.logs.map((log) => (
              <div key={log.id} className="asset-log">
                <div className="asset-log-header">
                  <span className="asset-log-action">{log.action}</span>
                  <span className="asset-log-date">{log.performed_on}</span>
                </div>
                <p className="asset-log-content">
                  {log.description} — {log.performed_by_name}
                </p>
              </div>
            ))
          ) : (
            <div className="asset-log-empty">No history yet</div>
          )}
        </Form>
        <div className="asset-modal-footer">
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button
            type="primary"
            onClick={() =>
              editForm.validateFields().then(async (v) => {
                try {
                  const payload = {
                    id: selectedAsset.id,
                    ...v,
                    brand: Array.isArray(v.brand) ? v.brand[0] : v.brand,
                  };
                  const r = await postData('/finance/update_asset/', payload);
                  if (r.success) {
                    message.success('Asset updated');
                    setEditModalOpen(false);
                    load();
                    loadStats();
                    loadOpts();
                  } else message.error(r.message || 'Failed');
                } catch {
                  message.error('Failed to update asset');
                }
              })
            }
          >
            Update Asset
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default AssetsManagement;
